import dayjs from 'dayjs';
import type { ButtonGroupItem } from '~/components/nrjx/button/group.vue';

export const useDateRange = () => {
  // Composables
  const { t } = useI18n();

  // Constants
  const inputsTypes = ['start', 'end'] as const;
  const aggregationLevelOptions: ButtonGroupItem[] = [
    {
      label: t('global.minute_short'),
      key: 'minute',
    },
    {
      label: t('global.hour_short'),
      key: 'hour',
    },
    {
      label: t('global.day_short'),
      key: 'day',
    },
    {
      label: t('global.week_short'),
      key: 'week',
    },
    {
      label: t('global.month_short'),
      key: 'month',
    },
  ];

  // Methods
  const shortcuts = (periodSelected: { start: string; end: string }) => {
    // Values
    const start = dayjs(periodSelected.start);
    const end = dayjs(periodSelected.end);

    return [
      {
        label: t('global.date_range.last_24_hours'),
        start: dayjs().subtract(24, 'hour').toDate(),
        end: dayjs().toDate(),
        selected: start.isSame(dayjs().subtract(24, 'hour'), 'day') && end.isSame(dayjs().set('minute', 0).toDate(), 'day'),
      },
      {
        label: t('global.date_range.last_x_days', { value: 7 }),
        start: dayjs().subtract(6, 'day').startOf('day').toDate(),
        end: dayjs().startOf('day').toDate(),
        selected: start.isSame(dayjs().subtract(6, 'day').startOf('day'), 'day') && end.isSame(dayjs().startOf('day'), 'day'),
      },
      {
        label: t('global.date_range.last_week'),
        start: dayjs().subtract(1, 'week').startOf('week').startOf('day').toDate(),
        end: dayjs().subtract(1, 'week').endOf('week').add(1, 'day').startOf('day').toDate(),
        selected:
          start.isSame(dayjs().subtract(1, 'week').startOf('week').startOf('day'), 'day') &&
          end.isSame(dayjs().subtract(1, 'week').endOf('week').add(1, 'day').startOf('day'), 'day'),
      },
      {
        label: t('global.date_range.current_month'),
        start: dayjs().startOf('month').startOf('day').toDate(),
        end: dayjs().startOf('day').toDate(),
        selected: start.isSame(dayjs().startOf('month').startOf('day'), 'day') && end.isSame(dayjs().startOf('day'), 'day'),
      },
      {
        label: t('global.date_range.last_month'),
        start: dayjs().subtract(1, 'month').startOf('month').startOf('day').toDate(),
        end: dayjs().subtract(1, 'month').endOf('month').add(1, 'day').startOf('day').toDate(),
        selected:
          start.isSame(dayjs().subtract(1, 'month').startOf('month').startOf('day'), 'day') &&
          end.isSame(dayjs().subtract(1, 'month').endOf('month').add(1, 'day').startOf('day'), 'day'),
      },
      {
        label: t('global.date_range.last_x_months', { value: 3 }),
        start: dayjs().subtract(3, 'month').startOf('month').startOf('day').toDate(),
        end: dayjs().subtract(1, 'month').endOf('month').add(1, 'day').startOf('day').toDate(),
        selected:
          start.isSame(dayjs().subtract(3, 'month').startOf('month').startOf('day'), 'day') &&
          end.isSame(dayjs().subtract(1, 'month').endOf('month').add(1, 'day').startOf('day'), 'day'),
      },
      {
        label: t('global.date_range.last_x_months', { value: 6 }),
        start: dayjs().subtract(6, 'month').startOf('month').startOf('day').toDate(),
        end: dayjs().subtract(1, 'month').endOf('month').add(1, 'day').startOf('day').toDate(),
        selected:
          start.isSame(dayjs().subtract(6, 'month').startOf('month').startOf('day'), 'day') &&
          end.isSame(dayjs().subtract(1, 'month').endOf('month').add(1, 'day').startOf('day'), 'day'),
      },
      {
        label: t('global.date_range.last_x_months', { value: 12 }),
        start: dayjs().subtract(12, 'month').startOf('month').startOf('day').toDate(),
        end: dayjs().subtract(1, 'month').endOf('month').add(1, 'day').startOf('day').toDate(),
        selected:
          start.isSame(dayjs().subtract(12, 'month').startOf('month').startOf('day'), 'day') &&
          end.isSame(dayjs().subtract(1, 'month').endOf('month').add(1, 'day').startOf('day'), 'day'),
      },
      {
        label: t('global.date_range.current_year'),
        start: dayjs().startOf('year').startOf('day').toDate(),
        end: dayjs().startOf('day').toDate(),
        selected: start.isSame(dayjs().startOf('year').startOf('day'), 'day') && end.isSame(dayjs().toDate(), 'day'),
      },
      {
        label: t('global.date_range.last_year'),
        start: dayjs().subtract(1, 'year').startOf('year').startOf('day').toDate(),
        end: dayjs().subtract(1, 'year').endOf('year').add(1, 'day').startOf('day').toDate(),
        selected:
          start.isSame(dayjs().subtract(1, 'year').startOf('year').startOf('day'), 'day') &&
          end.isSame(dayjs().subtract(1, 'year').endOf('year').add(1, 'day').startOf('day'), 'day'),
      },
    ];
  };

  return { aggregationLevelOptions, inputsTypes, shortcuts };
};
